import {
  Button,
  FormControl,
  OutlinedInput,
  InputLabel,
  FormHelperText,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Link,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal, { CommonModalProps, ModalContext } from "..";
import { assetService, userService } from "../../../Core/Api/services";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import { DbObjectCollectionInfos } from "../../../Core/Api/Types/db-object-collection-infos";
import { CollectionType } from "../../../Core/Api/Types/db-collection-type";
import ModalConfirmAction, { ModalConfirmActionElementProps } from "../ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { CollectionLocation } from "../../../Core/Api/Types/db-collection-location";
import { useTranslation } from "react-i18next";

export type ModalCreateCollectionElementProps = {
  onClose?: (props: DbObjectCollectionInfos) => void;
  type?: CollectionType;
  data?: DbObjectCollectionInfos;
};

export type ModalCreateCollectionProps = CommonModalProps<ModalCreateCollectionElementProps>;

const ModalCreateCollection = React.forwardRef<any, ModalCreateCollectionProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, data, type } = props;
  const navigate = useNavigate();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();

  const mode = data ? "edit" : "new";
  const [isStoreFront, setIsStoreFront] = useState<boolean>(type === CollectionType.Storefront ? true : false);
  const [groups, setGroups] = useState<GroupAdvancedInfos[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<GroupAdvancedInfos[]>([]);
  const [users, setUsers] = useState<DbUserBasicInfos[]>([]);
  const [collectionData, setCollectionData] = React.useState(
    data
      ? data
      : new DbObjectCollectionInfos({
          name: "",
          type: type,
          targetUserIds: [],
          targetGroupIds: [],
          isPublished: null,
          randomDisplay: null,
        })
  );

  const handleCollectionNameChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setCollectionData((prevState) => ({ ...prevState, name: e.target.value }));
  };

  const handleCollectionDescriptionChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setCollectionData((prevState) => ({ ...prevState, description: e.target.value }));
  };

  const handleDeleteCollection = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      commentLabel: t("modal:body.deleteCollectionConfirm"),
      title: t("modal:title.deleteCollection"),
      onConfirm: () => {
        assetService.DeleteCollection(collectionData.id).then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.collectionDeleted"), "success");
            navigate(isStoreFront ? routePaths.storefront.path : routePaths.collections.path);
          }
        });
      },
    });
  };

  const handleConfirmChanges: React.MouseEventHandler = () => {
    if (mode === "new") {
      assetService
        .CreateCollection(
          collectionData.name!,
          collectionData.description || "",
          collectionData.type!,
          collectionData.randomDisplay || undefined,
          collectionData.location || undefined,
          collectionData.isPublished || undefined,
          collectionData.targetGroupIds || [],
          collectionData.targetUserIds || []
        )
        .then((data) => {
          closeModal();
          onClose && onClose(collectionData);
          if (data.success) {
            enqueueCustomSnackbar(t("alert:success.collectionCreated"), "success");
            navigate(
              (isStoreFront ? routePaths.storefront.path : routePaths.collections.path) + "/" + `${data.objectId}`
            );
          } else enqueueCustomSnackbar(data.message!, "error");
        });
    } else {
      assetService
        .UpdateCollection(
          collectionData.id,
          collectionData.name!,
          collectionData.description || "",
          collectionData.type!,
          collectionData.location || undefined,
          collectionData.randomDisplay || false,
          collectionData.isPublished || false,
          collectionData.targetGroupIds || undefined,
          collectionData.targetUserIds || undefined,
          collectionData.objectIds || undefined
        )
        .then((data) => {
          closeModal();
          onClose && onClose(collectionData);
          if (data.success) {
            enqueueCustomSnackbar(t("alert:success.collectionUpdated"), "success");
            navigate(
              (isStoreFront ? routePaths.storefront.path : routePaths.collections.path) + "/" + `${data.objectId}`
            );
          } else enqueueCustomSnackbar(data.message!, "error");
        });
    }
  };

  //   const onChangePublicMode = () => {
  //     setCollectionData((prevState) => ({
  //       ...prevState,
  //       type: prevState.type === CollectionType.Storefront ? CollectionType.Storefront : CollectionType.Whishlist,
  //     }));
  //   };

  // const collectionCardPreviewProps: CollectionCardProps = {
  //     to: "",
  //     title: collectionData.name!.length ? collectionData.name! : "Ex: Series</br> of 2022",
  //     placeholder: (collectionData.files?.length || 0) <= 0,
  //     // cover: collectionData.files,
  //   };

  const getGroupLabel = (group) => {
    let label: string = group.name;
    let isFiltered: boolean = false;
    if (group.filterTitleIds !== null && group.filterTitleIds.length > 0) {
      label = label + "-titles";
      isFiltered = true;
    }
    if (group.labProviderIds !== null && group.labProviderIds.length > 0) {
      isFiltered ? (label = label + "/providers") : (label = label + "-providers");
      isFiltered = true;
    }
    if (group.filterContents !== null) {
      isFiltered ? (label = label + "/content") : (label = label + "-content");
      isFiltered = true;
    }
    return isFiltered ? label + "Filtered" : label;
  };

  useEffect(() => {
    Promise.all([
      assetService.GetCollectionStoreFront(),
      userService.GetGroupsList(),
      userService.GetAllUsersBasic(),
    ]).then(([collections, groups, users]) => {
      if (isStoreFront) {
        //Storefront
        setGroups(groups.groups!);

        const unavailableGroupIds = collections
          .filter((x) => x.id !== collectionData.id)
          .map((obj) => obj.targetGroupIds)
          .flat();

        groups.groups =
          groups.groups?.filter(
            (x) =>
              x.group?.enabled &&
              x.group.name !== "deleted" &&
              x.group.name !== "Deleted Account" &&
              !unavailableGroupIds.includes(x.id)
          ) || [];

        setFilteredGroups(groups.groups);
      } else {
        //Collection
        if (users.users) setUsers(users.users?.filter((x) => !x.deleted));
        if (groups.groups) setGroups(groups.groups);
      }
    });
  }, [collectionData]);

  return (
    <Modal
      title={mode === "new" ? t("modal:title.createCollection") : t("modal:title.editCollection")}
      reference={ref}
      closeModal={closeModal}
      width={700}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1.5}>
            <Button onClick={closeModal}>{t("common:cancel")}</Button>
            {mode === "edit" && (
              <Button onClick={handleDeleteCollection} variant="outlined" color="error">
                {t("common:delete")}
              </Button>
            )}
          </Stack>
          <Button disabled={collectionData.name === ""} onClick={handleConfirmChanges} variant="contained">
            {mode === "new" ? t("button:createNewCollection") : t("button:confirmChanges")}
          </Button>
        </Stack>
      }
    >
      <FormControl sx={{ mb: 3 }}>
        <InputLabel shrink htmlFor="rename-collection">
          {t("field:label.collectionName")}
        </InputLabel>
        <OutlinedInput
          placeholder={t("field:placeholder.collectionName")}
          value={collectionData.name}
          onChange={handleCollectionNameChange}
          id="rename-collection"
        />
        <FormHelperText>{t("field:helper.collectionNameMaxLength", { count: 40 })}</FormHelperText>
      </FormControl>

      <FormControl sx={{ mb: 3 }}>
        <InputLabel shrink htmlFor="desc-collection">
          {t("common:description")}
        </InputLabel>
        <OutlinedInput
          placeholder={t("common:description")}
          value={collectionData.description}
          onChange={handleCollectionDescriptionChange}
          id="description"
        />
      </FormControl>

      {groups && (
        <Stack spacing={2}>
          {mode === "new" && isStoreFront && (
            <Stack>
              <InputLabel sx={{ mb: 0 }}>{t("common:location")}</InputLabel>
              <Autocomplete
                options={Object.values(CollectionLocation)}
                noOptionsText={t("field:label.chooseLocation")}
                value={collectionData.location}
                onChange={(e, value) => {
                  if (value === CollectionLocation.Carousel) {
                    setCollectionData((prevState) => ({
                      ...prevState,
                      location: value,
                      isPublished: null,
                      targetGroupIds: [],
                    }));
                  } else setCollectionData((prevState) => ({ ...prevState, location: value }));
                }}
                renderInput={(params) => (
                  <TextField sx={{ mt: -2, mb: 1 }} {...params} placeholder={t("field:label.chooseLocation")} />
                )}
              />
            </Stack>
          )}

          <Stack>
            <InputLabel sx={{ mb: 0 }}>{t("common:groups")}</InputLabel>
            <Autocomplete
              options={collectionData.location === CollectionLocation.Carousel ? filteredGroups : groups || []}
              multiple
              getOptionLabel={(value: any) => getGroupLabel(value.group)}
              noOptionsText={t("field:label.findGroup")}
              value={groups?.filter((x) => collectionData.targetGroupIds?.includes(x.group!.id))}
              onChange={(e, value) => {
                setCollectionData((prevState) => ({ ...prevState, targetGroupIds: value.map((x) => x.group.id) }));
              }}
              renderInput={(params) => (
                <TextField sx={{ mt: -2, mb: 1 }} {...params} placeholder={t("field:placeholder.addGroup")} />
              )}
            />
            <Stack spacing={1.5} direction="row" justifyContent="space-between">
              <Link
                underline="hover"
                onClick={(e) =>
                  setCollectionData((prevState) => ({ ...prevState, targetGroupIds: groups.map((x) => x.group!.id) }))
                }
              >
                {t("common:selectAll")}
              </Link>
              <Link
                underline="hover"
                onClick={(e) => setCollectionData((prevState) => ({ ...prevState, targetGroupIds: [] }))}
              >
                {t("common:unselectAll")}
              </Link>
            </Stack>
          </Stack>
        </Stack>
      )}

      {!isStoreFront && users && (
        <>
          <InputLabel sx={{ mt: 1 }}>{t("common:users")}</InputLabel>
          <Autocomplete
            options={users || []}
            multiple
            getOptionLabel={(value: any) => value.name}
            noOptionsText="Find user"
            value={users?.filter((x) => collectionData.targetUserIds?.includes(x.id))}
            onChange={(e, value) => {
              setCollectionData((prevState) => ({ ...prevState, targetUserIds: value.map((x) => x.id) }));
            }}
            renderInput={(params) => <TextField sx={{ mt: -2, mb: 1 }} {...params} placeholder={t("common:addUser")} />}
          />
          <Stack spacing={1.5} direction="row" justifyContent="space-between">
            <Link
              underline="hover"
              onClick={(e) =>
                setCollectionData((prevState) => ({ ...prevState, targetUserIds: users.map((x) => x.id) }))
              }
            >
              {t("common:selectAll")}
            </Link>
            <Link
              underline="hover"
              onClick={(e) => setCollectionData((prevState) => ({ ...prevState, targetUserIds: [] }))}
            >
              {t("common:unselectAll")}
            </Link>
          </Stack>
        </>
      )}

      {isStoreFront && (
        <Stack spacing={1.5} mt={2}>
          {collectionData.location === CollectionLocation.List && (
            <OutlinedCheckbox
              checked={collectionData.isPublished || false}
              onClick={() =>
                setCollectionData((prevState) => ({ ...prevState, isPublished: !collectionData.isPublished }))
              }
            >
              <Typography fontWeight={500}>{t("modal:body.publishCollection")}</Typography>
              <Typography fontWeight={500} variant="body2" color={"text.tier"}>
                {t("modal:modalCreateCollection.body.publishCollectionText")}
              </Typography>
            </OutlinedCheckbox>
          )}

          <OutlinedCheckbox
            checked={collectionData.randomDisplay || false}
            onClick={() =>
              setCollectionData((prevState) => ({ ...prevState, randomDisplay: !collectionData.randomDisplay }))
            }
          >
            <Typography fontWeight={500}>{t("modal:modalCreateCollection.body.enableRandomDisplay")}</Typography>
            <Typography fontWeight={500} variant="body2" color={"text.tier"}>
              {t("modal:modalCreateCollection.body.enabledRandomDisplayText")}
            </Typography>
          </OutlinedCheckbox>
        </Stack>
      )}

      {/* <OutlinedCheckbox
        onClick={onChangePublicMode}
        checked={collectionData.type === CollectionType.Storefront ? true : false}
      >
        <Typography color="text.primary" fontWeight={500}>
          Public collection
        </Typography>
        <Typography color="text.tier">Public collections are published to your customer's storefront</Typography>
      </OutlinedCheckbox>
      {/*<Typography variant="subtitle2" mb={1.5} fontWeight={600}>
        Graphic assets
      </Typography>
      <FormControl sx={{ mb: 2 }}>
        <InputLabel shrink htmlFor="feature-title">
          Select poster from titles
        </InputLabel>
        <Select displayEmpty IconComponent={ExpandMoreRoundedIcon} value="" id="feature-title" variant="outlined">
          <MenuItem value="">None</MenuItem>
          <MenuItem value="title">Kung-fu panda</MenuItem>
          <MenuItem value="quality">Spiderhead</MenuItem>
        </Select>
    </FormControl>*/}
      {/* <Typography variant="subtitle2" mb={1.5} fontWeight={600}>
        Upload manually
      </Typography>
      <Stack spacing={1.5} mb={3}>
        <UploadCard
          title="Collection Card"
          resolution="250x500"
          ratio="1:3"
          objectId={0}
          format={{
            id: 1,
            isUploaded: false,
            maxFileSizeMb: 100,
            name: "example format",
            userHasRighToDelete: false,
            acceptsFiles: { ["application/octet-stream"]: ["pdf"] },
            description: "Example description",
            isUserDataMandatory: false,
          }}
          {...(collectionData.cover.length > 0 && { cover: collectionData.cover })}
          userHasRightToDelete={true}
        />
        <UploadCard
          title="Collection Card"
          resolution="250x500"
          ratio="1:3"
          objectId={0}
          format={{
            id: 1,
            isUploaded: false,
            maxFileSizeMb: 100,
            name: "example format",
            userHasRighToDelete: false,
            acceptsFiles: { ["application/octet-stream"]: ["pdf"] },
            description: "Example description",
            isUserDataMandatory: false,
          }}
          {...(collectionData.cover.length > 0 && { cover: collectionData.cover })}
          userHasRightToDelete={true}
        />
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Card variant="outlined" sx={{ p: 1.5 }}>
          <Typography color="text.primary" fontWeight={500} mb={2}>
            Collection card preview
          </Typography>
          <CollectionCard {...collectionCardPreviewProps} />
        </Card>
        <Button variant="outlined" size="small" startIcon={<RefreshOutlinedIcon />} onClick={onRefreshAssets}>
          Refresh assets
        </Button>
      </Stack> */}
    </Modal>
  );
});

export default ModalCreateCollection;
